import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import HumanTaskDisplay from './dsl-components/HumanTaskDisplay';
import HumanTaskPhoneDialer from './dsl-components/HumanTaskPhoneDialer';
import HumanTaskUserInfo from './dsl-components/HumanTaskUserInfo';
import { AppTab } from './appLayoutConfig';
import './human-task-wrapper.scss';

interface HumanTaskWrapperProps {
  taskId: string;
  threadId?: string;
  tabId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  onTabsReady?: (tabs: AppTab[]) => void;
}

interface TaskData {
  id: string;
  taskType: 'phone_call' | 'web_action' | 'in_person' | 'browse_session';
  title: string;
  description: string;
  status: string;
  taskDetails: any;
  managerThreadId: string | null;
  acceptedAt?: string | null;
  connecteamStatus?: string | null;
  connecteamWorkerName?: string | null;
  connecteamTaskId?: string | null;
  hasCredentials?: boolean;
  hasMandatePayment?: boolean;
  hasVcc?: boolean;
  mandatePaymentMethod?: string | null;
}

/** Tabs to show for phone call tasks */
const PHONE_CALL_TABS: AppTab[] = [
  { id: 'details', label: 'Instructions' },
  { id: 'phone', label: 'Phone Dialer' },
  { id: 'user-info', label: 'Info' },
];

/** Tabs to show for web action tasks */
const WEB_ACTION_TABS: AppTab[] = [
  { id: 'details', label: 'Instructions' },
  { id: 'user-info', label: 'Info' },
];

/** Tabs to show for in-person tasks */
const IN_PERSON_TABS: AppTab[] = [
  { id: 'details', label: 'Instructions' },
  { id: 'user-info', label: 'Info' },
];

/** Tabs to show for browse session tasks */
const BROWSE_SESSION_TABS: AppTab[] = [
  { id: 'details', label: 'Instructions' },
  { id: 'user-info', label: 'Info' },
];

/**
 * Wrapper component that fetches human task data and renders content.
 * Calls onTabsReady to tell CanvasAssets which tabs to show in the header.
 * - Phone call tasks: Shows task details and phone dialer tabs
 * - Web action tasks: Shows only task details tab
 */
const HumanTaskWrapper: React.FC<HumanTaskWrapperProps> = ({
  taskId,
  threadId,
  tabId = 'details',
  updateThreadViewVisualDescription,
  onTabsReady,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [taskData, setTaskData] = useState<TaskData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastTabsRef = useRef<string>('');

  const fetchTask = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/human-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.task) {
        const task = response.data.task;
        setTaskData({
          id: task.id,
          taskType: task.taskType,
          title: task.title,
          description: task.description,
          status: task.status,
          taskDetails: task.taskDetails,
          managerThreadId: task.managerThreadId || null,
          acceptedAt: task.acceptedAt || null,
          connecteamStatus: task.connecteamStatus || null,
          connecteamWorkerName: task.connecteamWorkerName || null,
          connecteamTaskId: task.connecteamTaskId || null,
          hasCredentials: !!response.data.hasCredentials,
          hasMandatePayment: !!response.data.hasMandatePayment,
          hasVcc: !!response.data.hasVcc,
          mandatePaymentMethod: response.data.mandatePaymentMethod || null,
        });

        // Update visual description if callback provided
        if (updateThreadViewVisualDescription) {
          updateThreadViewVisualDescription(`Human Task: ${task.title}`);
        }
      } else {
        setError('Failed to load task');
      }
    } catch (err) {
      console.error('Error fetching human task:', err);
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [taskId, getAccessTokenSilently, updateThreadViewVisualDescription]);

  // Reset state when taskId changes
  useEffect(() => {
    setLoading(true);
    setTaskData(null);
    setError(null);
    lastTabsRef.current = ''; // Reset so onTabsReady will be called for new task
    fetchTask();
  }, [taskId, fetchTask]);

  const isTerminalStatus = taskData
    ? ['completed', 'cancelled', 'failed'].includes(taskData.status)
    : false;

  // Notify parent of tabs when task data changes
  useEffect(() => {
    if (!taskData || !onTabsReady) return;

    // For terminal tasks, never show the phone dialer tab
    let tabs: AppTab[];
    if (isTerminalStatus) {
      tabs = WEB_ACTION_TABS; // Instructions + Info only
    } else {
      tabs =
        taskData.taskType === 'phone_call'
          ? PHONE_CALL_TABS
          : taskData.taskType === 'in_person'
            ? IN_PERSON_TABS
            : taskData.taskType === 'browse_session'
              ? BROWSE_SESSION_TABS
              : WEB_ACTION_TABS;
    }
    const tabsKey = JSON.stringify(tabs);

    if (tabsKey !== lastTabsRef.current) {
      lastTabsRef.current = tabsKey;
      onTabsReady(tabs);
    }
  }, [taskData, onTabsReady, isTerminalStatus]);

  if (loading) {
    return (
      <div className='human-task-wrapper loading'>
        <p>Loading task...</p>
      </div>
    );
  }

  if (error || !taskData) {
    return (
      <div className='human-task-wrapper error'>
        <p>{error || 'Task not found'}</p>
      </div>
    );
  }

  const taskTypeLabel =
    taskData.taskType === 'phone_call'
      ? 'Phone Call'
      : taskData.taskType === 'in_person'
        ? 'In-Person'
        : taskData.taskType === 'browse_session'
          ? 'Browse Session'
          : 'Web Action';

  const isPhoneCall = taskData.taskType === 'phone_call';
  const showPhoneDialer = isPhoneCall && !isTerminalStatus;

  // Render all tabs simultaneously so components stay mounted (preserving
  // active Twilio calls, etc.) — only the selected tab is visible.
  return (
    <>
      <div style={{ display: tabId === 'details' ? 'contents' : 'none' }}>
        <HumanTaskDisplay
          taskId={taskData.id}
          taskType={taskData.taskType}
          taskTypeLabel={taskTypeLabel}
          title={taskData.title}
          description={taskData.description}
          taskDetails={taskData.taskDetails}
          status={taskData.status}
          connecteamStatus={taskData.connecteamStatus}
          connecteamWorkerName={taskData.connecteamWorkerName}
          readOnly={isTerminalStatus}
          acceptedAt={taskData.acceptedAt}
          connecteamTaskId={taskData.connecteamTaskId}
          hasCredentials={taskData.hasCredentials}
          hasMandatePayment={taskData.hasMandatePayment}
          hasVcc={taskData.hasVcc}
          mandatePaymentMethod={taskData.mandatePaymentMethod}
          threadId={threadId}
          onTaskClosed={fetchTask}
        />
      </div>

      {showPhoneDialer && (
        <div style={{ display: tabId === 'phone' ? 'contents' : 'none' }}>
          <HumanTaskPhoneDialer
            taskId={taskData.id}
            phoneNumber={taskData.taskDetails?.phoneNumber || ''}
            contactName={taskData.taskDetails?.contactName}
          />
        </div>
      )}

      <div style={{ display: tabId === 'user-info' ? 'contents' : 'none' }}>
        <HumanTaskUserInfo taskId={taskData.id} />
      </div>
    </>
  );
};

export default HumanTaskWrapper;
